/// <reference lib="dom" />
/**
 * Widgets shared by the Tileset tool and the editor's tile case fixer: sprite drawing, sprite picker,
 * weighted choice lists. Written with createElement (no JSX), see TilesetTool.
 */
import { createElement as h, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { PaletteEntry } from '../model/format.ts';
import { gameData, onTexturesChanged, readyImage, textureVersion } from '../data/gameData.ts';
import { customEntries } from '../data/customTiles.ts';
import { flagMatrix, shapePoly } from './shapes.ts';

export { shapePoly };
import { FILL, NONE, type StyleOverride, type TerrainStyleData } from './types.ts';
import { compiledStyle } from './index.ts';
import { variantSprites } from './variants.ts';

// ------------------------------------------------------------------ drawing helpers

const imageCache = new Map<string, HTMLImageElement>();

export function texImage(tex: string, onLoad: () => void): HTMLImageElement | HTMLCanvasElement | null {
  let img: HTMLImageElement | HTMLCanvasElement | undefined;
  try {
    img = gameData().images.get(tex);
  } catch {
    /* game data not loaded: fall back to our own image */
  }
  if (!img) {
    let own = imageCache.get(tex);
    if (!own) {
      own = new Image();
      own.src = `/game-data/tex/${tex}.png`;
      imageCache.set(tex, own);
    }
    img = own;
  }
  const ready = readyImage(img);
  if (ready) return ready;
  img.addEventListener('load', onLoad, { once: true });
  return null;
}

/** Re-render when runtime textures (custom tiles) change. */
export function useTextureVersion(): number {
  return useSyncExternalStore(onTexturesChanged, textureVersion);
}

export function spriteGeom(pe: PaletteEntry) {
  let gd;
  try {
    gd = gameData();
  } catch {
    return null;
  }
  const texDef = gd.sprites.textures[pe.tex];
  const def = gd.spriteByName.get(`${pe.tex}|${pe.sprite}`)?.def;
  if (!texDef || !def) return null;
  const ppu = texDef.ppu || 16;
  const [rx, ry, rw, rh] = def.r;
  const w = rw / ppu, h2 = rh / ppu;
  return { texDef, rx, ry, rw, rh, w, h: h2, x0: -def.p[0] * w, y0: -def.p[1] * h2 };
}

/**
 * Draw a sprite with tile flags. The ctx transform must map world units with y up and the
 * cell centre at the origin.
 */
export function drawSpriteAt(ctx: CanvasRenderingContext2D, pe: PaletteEntry, flags: number, redraw: () => void): boolean {
  const g = spriteGeom(pe);
  if (!g) return false;
  const img = texImage(pe.tex, redraw);
  if (!img) return false;
  ctx.save();
  if (flags) {
    const [a, b, c, d] = flagMatrix(flags);
    ctx.transform(a, c, b, d, 0, 0);
  }
  ctx.scale(1, -1);
  ctx.drawImage(img, g.rx, g.texDef.h - g.ry - g.rh, g.rw, g.rh, g.x0, -(g.y0 + g.h), g.w, g.h);
  ctx.restore();
  return true;
}

export function useCanvas(size: [number, number], draw: (ctx: CanvasRenderingContext2D, redraw: () => void) => void, deps: unknown[]) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.imageSmoothingEnabled = false;
    draw(ctx, () => setTick((t) => t + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, size[0], size[1]]);
  return ref;
}

export function SpriteThumb({ pe, flags, size = 40 }: { pe: PaletteEntry | null; flags: number; size?: number }) {
  const tv = useTextureVersion();
  const ref = useCanvas(
    [size, size],
    (ctx, redraw) => {
      if (!pe) return;
      const g = spriteGeom(pe);
      const ext = g ? Math.max(0.5, Math.abs(g.x0), Math.abs(g.x0 + g.w), Math.abs(g.y0), Math.abs(g.y0 + g.h)) : 0.5;
      const k = size / (2 * ext);
      ctx.setTransform(k, 0, 0, -k, size / 2, size / 2);
      drawSpriteAt(ctx, pe, flags, redraw);
    },
    [pe?.tex, pe?.sprite, flags, tv],
  );
  return h('canvas', { ref, width: size, height: size, className: 'tt-thumb' });
}

// ------------------------------------------------------------------ style helpers

export function spriteTable(d: TerrainStyleData, ov: StyleOverride): PaletteEntry[] {
  return [...d.sprites, ...(ov.extraSprites ?? [])];
}

export function variantPe(d: TerrainStyleData, table: PaletteEntry[], si: number, variant: number): PaletteEntry | null {
  const st = variant > 0 ? compiledStyle(d.id) : null;
  return (st && variantSprites(st, variant)[si]) || (table[si] ?? null);
}

export function pairs(arr: number[] | undefined): [number, number][] {
  const out: [number, number][] = [];
  if (arr) for (let i = 0; i + 1 < arr.length; i += 2) out.push([arr[i], arr[i + 1]]);
  return out;
}


export function ChoiceThumb({ d, table, choice, variant, size = 40 }: { d: TerrainStyleData; table: PaletteEntry[]; choice: number; variant: number; size?: number }) {
  if (choice === NONE) return h('div', { className: 'tt-special', style: { width: size, height: size } }, 'none');
  if (choice === FILL) return h('div', { className: 'tt-special fill', style: { width: size, height: size } }, 'fill');
  return h(SpriteThumb, { pe: variantPe(d, table, choice >> 3, variant), flags: choice & 7, size });
}

export function choiceLabel(table: PaletteEntry[], choice: number): string {
  if (choice === NONE) return 'nothing';
  if (choice === FILL) return 'interior fill';
  const pe = table[choice >> 3];
  const f = choice & 7;
  const fl = [f & 1 ? 'flipX' : '', f & 2 ? 'flipY' : '', f & 4 ? 'rot' : ''].filter(Boolean).join(' ');
  return (pe?.sprite ?? '?') + (fl ? ` (${fl})` : '');
}

// ------------------------------------------------------------------ small widgets

export function Num({ value, onChange, step = 1, min, max, width = 64 }: { value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number; width?: number }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return h('input', {
    className: 'tt-num',
    type: 'number',
    inputMode: 'decimal',
    value: text,
    step,
    min,
    max,
    style: { width },
    onChange: (e: { target: HTMLInputElement }) => {
      setText(e.target.value);
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) onChange(v);
    },
  });
}

export function Toggle({ value, onChange, label }: { value: boolean; onChange: (v: boolean) => void; label: string }) {
  return h('label', { className: 'tt-toggle' }, h('input', { type: 'checkbox', checked: value, onChange: (e: { target: HTMLInputElement }) => onChange(e.target.checked) }), h('span', null, label));
}

export function Btn({ onClick, children, kind, title, disabled }: { onClick: () => void; children?: ReactNode; kind?: 'primary' | 'danger' | 'ghost'; title?: string; disabled?: boolean }) {
  return h('button', { className: `tt-btn ${kind ?? ''}`, onClick, title, disabled }, children);
}

// ------------------------------------------------------------------ sprite picker

type PickerView = 'sheet' | 'list';
const PICKER_VIEW = 'ple:picker-view';
const PICKER_SCALE = 'ple:picker-scale';

/** The style's whole tileset sheet: tap a sprite on it to pick it. */
function SheetView({ tex, scale, onPick }: { tex: string; scale: number; onPick: (pe: PaletteEntry) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [, setLoaded] = useState(0);
  useTextureVersion();
  let texDef: { w: number; h: number; sprites: { n: string; r: number[] }[] } | undefined;
  try {
    texDef = gameData().sprites.textures[tex];
  } catch {
    texDef = undefined;
  }
  const spriteAt = (x: number, y: number) => {
    if (!texDef) return null;
    for (const s of texDef.sprites) {
      const top = texDef.h - s.r[1] - s.r[3];
      if (x >= s.r[0] && x < s.r[0] + s.r[2] && y >= top && y < top + s.r[3]) return s.n;
    }
    return null;
  };
  useEffect(() => {
    const c = ref.current;
    if (!c || !texDef) return;
    c.width = texDef.w * scale;
    c.height = texDef.h * scale;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.fillStyle = '#0d0d12';
    g.fillRect(0, 0, c.width, c.height);
    const img = texImage(tex, () => setLoaded((n) => n + 1));
    if (img) g.drawImage(img, 0, 0, c.width, c.height);
    g.strokeStyle = 'rgba(255,255,255,0.08)';
    for (const s of texDef.sprites) g.strokeRect(s.r[0] * scale + 0.5, (texDef.h - s.r[1] - s.r[3]) * scale + 0.5, s.r[2] * scale - 1, s.r[3] * scale - 1);
    const hs = hover ? texDef.sprites.find((s) => s.n === hover) : undefined;
    if (hs) {
      g.strokeStyle = '#c9973c';
      g.lineWidth = 2;
      g.strokeRect(hs.r[0] * scale + 1, (texDef.h - hs.r[1] - hs.r[3]) * scale + 1, hs.r[2] * scale - 2, hs.r[3] * scale - 2);
      g.lineWidth = 1;
    }
  });
  if (!texDef) return h('div', { className: 'tt-dim small' }, 'The tileset sheet is not loaded.');
  const at = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = ref.current!.getBoundingClientRect();
    return [Math.floor((e.clientX - r.left) / scale), Math.floor((e.clientY - r.top) / scale)];
  };
  return h(
    'div',
    { className: 'tt-sheet' },
    h('canvas', {
      ref,
      className: 'tt-sheet-canvas',
      style: { width: texDef.w * scale, height: texDef.h * scale },
      title: hover ?? undefined,
      onPointerMove: (e: { clientX: number; clientY: number }) => {
        const n = spriteAt(...at(e));
        if (n !== hover) setHover(n);
      },
      onPointerLeave: () => setHover(null),
      onClick: (e: { clientX: number; clientY: number }) => {
        const n = spriteAt(...at(e));
        if (n) onPick({ tex, sprite: n });
      },
    }),
  );
}

export function SpritePicker({ d, table, variant, allowFill, allowNone, onPick, onPickSpecial, onClose }: { d: TerrainStyleData; table: PaletteEntry[]; variant: number; allowFill: boolean; allowNone: boolean; onPick: (pe: PaletteEntry, flags: number) => void; onPickSpecial: (c: number) => void; onClose: () => void }) {
  const [flags, setFlags] = useState(0);
  const [all, setAll] = useState(false);
  const [limit, setLimit] = useState(120);
  const [view, setViewState] = useState<PickerView>(() => (localStorage.getItem(PICKER_VIEW) === 'list' ? 'list' : 'sheet'));
  const [scale, setScaleState] = useState(() => Math.max(1, Math.min(4, parseInt(localStorage.getItem(PICKER_SCALE) ?? '2', 10) || 2)));
  const setView = (v: PickerView) => {
    localStorage.setItem(PICKER_VIEW, v);
    setViewState(v);
  };
  const setScale = (s: number) => {
    localStorage.setItem(PICKER_SCALE, String(s));
    setScaleState(s);
  };
  // sprites that are not on the tileset sheet: tiles drawn for the style, sprites of other textures
  const extras = useMemo(() => {
    const seen = new Set<string>();
    const out: PaletteEntry[] = [];
    for (const pe of [...table, ...customEntries(d.id)]) {
      const k = pe.tex + '|' + pe.sprite;
      if (pe.tex === d.tex || seen.has(k)) continue;
      seen.add(k);
      out.push(pe);
    }
    return out;
  }, [d, table]);
  const list = useMemo(() => {
    const seen = new Set(table.map((p) => p.tex + '|' + p.sprite));
    const out: PaletteEntry[] = [...table];
    for (const pe of customEntries(d.id)) if (!seen.has(pe.tex + '|' + pe.sprite)) out.push(pe);
    if (all) {
      try {
        const tex = gameData().sprites.textures[d.tex];
        for (const s of tex?.sprites ?? []) if (!seen.has(d.tex + '|' + s.n)) out.push({ tex: d.tex, sprite: s.n });
      } catch {
        /* no game data */
      }
    }
    return out;
  }, [d, table, all]);
  return h(
    'div',
    { className: 'tt-modal', onClick: onClose },
    h(
      'div',
      { className: 'tt-picker', onClick: (e: { stopPropagation: () => void }) => e.stopPropagation() },
      h(
        'div',
        { className: 'tt-row wrap' },
        h('strong', null, 'Pick a sprite'),
        h(Toggle, { value: !!(flags & 1), onChange: (v) => setFlags((flags & ~1) | (v ? 1 : 0)), label: 'flip X' }),
        h(Toggle, { value: !!(flags & 2), onChange: (v) => setFlags((flags & ~2) | (v ? 2 : 0)), label: 'flip Y' }),
        h(Toggle, { value: !!(flags & 4), onChange: (v) => setFlags((flags & ~4) | (v ? 4 : 0)), label: 'rotate' }),
        h(
          'div',
          { className: 'tt-seg' },
          h('button', { className: view === 'sheet' ? 'active' : '', title: 'The whole tileset sheet, with the tiles drawn for this style below it', onClick: () => setView('sheet') }, 'Tileset sheet'),
          h('button', { className: view === 'list' ? 'active' : '', title: 'The sprites this style uses, one by one', onClick: () => setView('list') }, 'List'),
        ),
        view === 'list' && h(Toggle, { value: all, onChange: setAll, label: 'whole texture' }),
        view === 'sheet' && h('div', { className: 'tt-seg' }, ...[1, 2, 3, 4].map((k) => h('button', { key: k, className: scale === k ? 'active' : '', onClick: () => setScale(k) }, `${k}×`))),
        h('span', { className: 'tt-grow' }),
        allowNone && h(Btn, { onClick: () => onPickSpecial(NONE) }, 'nothing'),
        allowFill && h(Btn, { onClick: () => onPickSpecial(FILL) }, 'interior fill'),
        h(Btn, { onClick: onClose, kind: 'ghost' }, 'Cancel'),
      ),
      view === 'sheet' &&
        h(
          'div',
          { className: 'tt-picker-sheet' },
          h(SheetView, { tex: d.tex, scale, onPick: (pe) => onPick(pe, flags) }),
          h('h4', { className: 'tt-picker-sub' }, `New tiles (${extras.length})`),
          extras.length
            ? h(
                'div',
                { className: 'tt-picker-grid flat' },
                extras.map((pe) => h('button', { key: pe.tex + pe.sprite, className: 'tt-pick', title: pe.sprite, onClick: () => onPick(pe, flags) }, h(SpriteThumb, { pe, flags, size: 44 }))),
              )
            : h('div', { className: 'tt-dim small' }, 'No tiles drawn for this style yet.'),
        ),
      view === 'list' && h(
        'div',
        { className: 'tt-picker-grid' },
        list.slice(0, limit).map((pe, i) =>
          h(
            'button',
            { key: pe.tex + pe.sprite, className: 'tt-pick', title: pe.sprite, onClick: () => onPick(pe, flags) },
            h(SpriteThumb, { pe: i < table.length ? variantPe(d, table, i, variant) : pe, flags, size: 44 }),
          ),
        ),
        list.length > limit && h(Btn, { onClick: () => setLimit(limit + 240) }, `more (${list.length - limit})`),
      ),
    ),
  );
}



export function ChoiceList({ d, table, variant, arr, learned, onChange, onAdd }: { d: TerrainStyleData; table: PaletteEntry[]; variant: number; arr: number[]; learned?: number[]; onChange: (arr: number[]) => void; onAdd: () => void }) {
  const ps = pairs(arr);
  const total = ps.reduce((a, [, w]) => a + Math.max(0, w), 0) || 1;
  return h(
    'div',
    { className: 'tt-choices' },
    ps.map(([c, w], i) =>
      h(
        'div',
        { key: c + ':' + i, className: `tt-choice ${w <= 0 ? 'off' : ''}`, title: choiceLabel(table, c) },
        h(ChoiceThumb, { d, table, choice: c, variant }),
        h('div', { className: 'tt-pct' }, `${Math.round((100 * Math.max(0, w)) / total)}%`),
        h(Num, {
          value: w,
          min: 0,
          onChange: (v) => {
            const n = [...arr];
            n[i * 2 + 1] = Math.max(0, v);
            onChange(n);
          },
        }),
        h(
          'button',
          {
            className: 'tt-x',
            title: 'remove',
            onClick: () => {
              const n = [...arr];
              n.splice(i * 2, 2);
              onChange(n);
            },
          },
          '×',
        ),
      ),
    ),
    h('button', { className: 'tt-add', onClick: onAdd, title: 'add a choice' }, '+'),
    learned && learned.join(',') !== arr.join(',') && h(Btn, { kind: 'ghost', onClick: () => onChange([...learned]), title: 'back to the learned choices' }, 'revert'),
  );
}

export const TT_CSS = `
.tt-root{position:relative;display:flex;flex-direction:column;width:min(1200px,100vw);height:min(900px,100vh);max-height:100dvh;background:#181a21;color:#d8dce6;font:14px/1.35 system-ui,-apple-system,Segoe UI,sans-serif;border:1px solid #2e323d;border-radius:10px;overflow:hidden;box-shadow:0 10px 40px #0009}
@media (max-width:700px){.tt-root{border-radius:0;border:0;height:100dvh}}
.tt-head{display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:10px 12px;background:#20232c;border-bottom:1px solid #2e323d}
.tt-head h2{margin:0 8px 0 0;font-size:16px;font-weight:600}
.tt-body{flex:1;overflow:auto;padding:12px;-webkit-overflow-scrolling:touch}
.tt-tabs{display:flex;gap:4px;padding:6px 12px 0;background:#1c1f27;border-bottom:1px solid #2e323d;overflow-x:auto}
.tt-tabs button{background:none;border:0;border-bottom:2px solid transparent;color:#9aa1b2;padding:10px 14px;min-height:42px;font-size:14px;cursor:pointer;white-space:nowrap}
.tt-tabs button.active{color:#fff;border-bottom-color:#e0b25a}
.tt-btn{background:#2c303b;color:#e3e6ee;border:1px solid #3a3f4c;border-radius:6px;padding:8px 12px;min-height:38px;font-size:14px;cursor:pointer;touch-action:manipulation}
.tt-btn:hover{background:#353a47}.tt-btn:disabled{opacity:.45;cursor:default}
.tt-btn.primary{background:#c9973c;border-color:#c9973c;color:#1a1a1a;font-weight:600}
.tt-btn.danger{background:#5a2b2b;border-color:#733}
.tt-btn.ghost{background:none}
.tt-row{display:flex;gap:8px;align-items:center}.tt-row.wrap{flex-wrap:wrap;margin-bottom:8px}.tt-row.center{justify-content:center;margin:12px}
.tt-col{display:flex;flex-direction:column;gap:8px}
.tt-grow{flex:1}
.tt-dim{color:#8a91a3}.small{font-size:12px}
.tt-help{color:#8a91a3;font-size:13px;margin:4px 0 10px;max-width:900px}
.tt-select,.tt-search,.tt-num{background:#111318;color:#e3e6ee;border:1px solid #3a3f4c;border-radius:6px;padding:7px 8px;min-height:38px;font-size:14px}
.tt-search{min-width:180px}
.tt-seg{display:inline-flex;border:1px solid #3a3f4c;border-radius:6px;overflow:hidden;flex-wrap:wrap}
.tt-seg button{background:#22252e;color:#aab0bf;border:0;border-right:1px solid #3a3f4c;padding:8px 10px;min-height:38px;cursor:pointer;font-size:13px}
.tt-seg button:last-child{border-right:0}.tt-seg button.active{background:#3b3427;color:#f2d08c}
.tt-toggle{display:inline-flex;gap:6px;align-items:center;min-height:38px;cursor:pointer;user-select:none}
.tt-toggle input{width:20px;height:20px;accent-color:#c9973c}
.tt-cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(330px,1fr));gap:10px}
.tt-card{display:flex;gap:10px;align-items:flex-start;background:#1f222b;border:1px solid #2c303b;border-radius:8px;padding:8px}
.tt-card.edited{border-color:#8a6a2c}.tt-card.off{opacity:.5}
.tt-card-head{display:flex;flex-direction:column;align-items:center;gap:4px;min-width:64px;word-break:break-all;text-align:center}
.tt-diagram,.tt-thumb,.tt-stamp,.tt-preview{image-rendering:pixelated;image-rendering:crisp-edges}
.tt-diagram{border-radius:4px}
.tt-choices,.tt-tones{display:flex;flex-wrap:wrap;gap:6px;align-items:flex-start}
.tt-choice{position:relative;display:flex;flex-direction:column;align-items:center;gap:3px;background:#272b35;border-radius:6px;padding:5px 5px 6px}
.tt-choice.off{opacity:.45}
.tt-choice .tt-num{width:58px;min-height:32px;padding:4px 5px;text-align:center}
.tt-thumb{background:repeating-conic-gradient(#2d313c 0% 25%,#262a33 0% 50%) 50%/12px 12px;border-radius:3px}
.tt-pct{font-size:11px;color:#c7b27f}
.tt-x{position:absolute;top:-6px;right:-6px;width:24px;height:24px;border-radius:12px;border:0;background:#4b2f2f;color:#fbb;font-size:15px;line-height:22px;cursor:pointer;padding:0}
.tt-add{width:52px;height:52px;border:1px dashed #4a5060;border-radius:6px;background:none;color:#9aa1b2;font-size:24px;cursor:pointer}
.tt-special{display:flex;align-items:center;justify-content:center;font-size:11px;color:#9aa1b2;border:1px dashed #4a5060;border-radius:3px}
.tt-special.fill{background:#3a3326;color:#e0c48a;border-style:solid}
.tt-modal{position:absolute;inset:0;background:#000a;display:flex;align-items:center;justify-content:center;z-index:5}
.tt-picker{background:#1c1f27;border:1px solid #3a3f4c;border-radius:10px;padding:12px;width:min(900px,96%);max-height:90%;display:flex;flex-direction:column}
.tt-picker-grid{overflow:auto;display:flex;flex-wrap:wrap;gap:6px;padding-top:6px}
.tt-pick{background:#272b35;border:1px solid #333845;border-radius:6px;padding:4px;cursor:pointer;min-width:54px;min-height:54px}
.tt-pick:hover{border-color:#c9973c}
.tt-picker-sheet{overflow:auto;padding-top:6px;-webkit-overflow-scrolling:touch}
.tt-picker-grid.flat{overflow:visible}
.tt-picker-sub{font-size:13px;margin:12px 0 4px;color:#e9d3a3}
.tt-sheet{display:inline-block;line-height:0}
.tt-sheet-canvas{image-rendering:pixelated;image-rendering:crisp-edges;cursor:pointer;border-radius:4px}
.tt-preview-wrap{background:#14161c;border-bottom:1px solid #2e323d;padding:8px 12px;display:flex;gap:12px;align-items:flex-start;flex-wrap:wrap}
.tt-preview{width:min(552px,100%);height:auto;border-radius:6px;border:1px solid #2e323d}
.tt-card.stamp{align-items:center}
.tt-settings h3,.tt-root h3{font-size:14px;margin:18px 0 6px;color:#e9d3a3}
.tt-status{font-size:12px;color:#9fd49f}
`;
